import { useEffect } from "react";
import { useImmer } from "use-immer";
import { useParams } from "react-router-dom";
import { getBirthdayCouponCustomers } from "../api/BirthdayCouponsApi";
import { errorMessage } from "../../../utils";
import { useDebouncedCallback } from "use-debounce";
import qs from "qs";

export const useBirthdayCouponsV2ActiveCustomers = () => {
  const { id } = useParams();

  const [state, setState] = useImmer({
    isLoading: true,
    birthdayCouponCustomers: [],
    filterApplied: {
      searchText: "",
      status: "",
    },
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    sortBy: [],
  });

  useEffect(() => {
    getBirthdayCustomers({
      search: "",
      status: "",
      pagination: state.pagination,
      sortBy: state.sortBy,
    });
  }, []);

  const getBirthdayCustomers = async ({ search, pagination, sortBy, status }) => {
    let params = {};

    if (search) {
      params.search = search;
    }

    if (status) {
      params.status = status;
    }

    if (sortBy?.length > 0) {
      const sortByParams = sortBy.reduce(
        (obj, item) =>
          Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
        {},
      );
      params = {
        ...params,
        sort: sortByParams,
      };
    }

    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };

    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      const res = await getBirthdayCouponCustomers(id, query, pagination);
      if (res.success) {
        setState((draft) => {
          draft.isLoading = false;
          draft.birthdayCouponCustomers = res.data;
          draft.pagination = res.data.pagination;
        });
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || "Failed to fetch customers");
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const gotoPage = (pageIndex) => {
    const { pagination, sortBy, filterApplied } = state;
    getBirthdayCustomers({
      search: filterApplied.searchText,
      status: filterApplied.status,
      pagination: {
        pageIndex: pageIndex,
        pageSize: pagination.pageSize,
      },
      sortBy,
    });
  };

  const setPageSize = (e) => {
    const { sortBy, filterApplied } = state;
    getBirthdayCustomers({
      search: filterApplied.searchText,
      status: filterApplied.status,
      pagination: {
        pageIndex: 1,
        pageSize: e.target.value,
      },
      sortBy,
    });
  };

  const handleSort = (sortBy) => {
    const { pagination, filterApplied } = state;
    getBirthdayCustomers({
      search: filterApplied.searchText,
      status: filterApplied.status,
      pagination,
      sortBy,
    });
    setState((draft) => {
      draft.sortBy = sortBy;
    });
  };

  const onChangeCustomerFilter = (e) => {
    const { name, value } = e.target;
    setState((draft) => {
      draft.filterApplied[name] = value;
    });
    if (name === "searchText") {
      debouncedCustomerSearch(value);
    } else if (name === "status") {
      const { pagination, sortBy, filterApplied } = state;
      getBirthdayCustomers({
        search: filterApplied.searchText,
        status: value,
        pagination: {
          pageIndex: 1,
          pageSize: pagination.pageSize,
        },
        sortBy,
      });
    }
  };

  const debouncedCustomerSearch = useDebouncedCallback((value) => {
    const { pagination, sortBy, filterApplied } = state;
    getBirthdayCustomers({
      search: value,
      status: filterApplied.status,
      pagination: {
        pageIndex: 1,
        pageSize: pagination.pageSize,
      },
      sortBy,
    });
  }, 1000);

  const resetCustomerFilters = () => {
    getBirthdayCustomers({
      search: "",
      status: "",
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
    setState((draft) => {
      draft.filterApplied.searchText = "";
      draft.filterApplied.status = "";
    });
  };

  const refreshData = () => {
    const { searchText, status } = state.filterApplied;
    getBirthdayCustomers({
      search: searchText,
      status: status,
      pagination: state.pagination,
      sortBy: state.sortBy,
    });
  };

  return {
    state,
    gotoPage,
    setPageSize,
    handleSort,
    onChangeCustomerFilter,
    resetCustomerFilters,
    refreshData,
  };
};
